import { useState, useEffect, useRef } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useAssistantStore } from '@/stores/assistantStore';
import type { QuoteResultAction as Descriptor } from '@/lib/quoteResultAction';
import {
  quoteEditorPath,
  quoteResultScope,
  revalidateQuoteResult,
} from '@/lib/quoteResultNavigation';
import { useCurrentQuoteNavigation } from '@/hooks/useCurrentQuoteNavigation';

export function QuoteResultAction({ action }: { action: Descriptor }) {
  const navigate = useNavigate();
  const location = useLocation();
  const destination = useRef<string | null>(null);
  useEffect(() => {
    if (destination.current === location.pathname) {
      destination.current = null;
      useAssistantStore.getState().setOpen(false);
    }
  }, [location]);
  const { t } = useTranslation('assistant');
  const isCurrent = useCurrentQuoteNavigation();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const allowed = useAuthStore((auth) => quoteResultScope(action, auth) !== undefined);
  const open = async (services: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setFailed(false);
    try {
      const checked = await revalidateQuoteResult(action);
      if (!isCurrent()) return;
      // Keep return metadata in browser history state, independently of one-shot services focus.
      destination.current = quoteEditorPath(action);
      void navigate(destination.current, {
        state: {
          quoteReturn: { action: checked.quoteResult, family: checked.scope.family },
          ...(services ? { openSection: 'services' } : {}),
        },
      });
    } catch {
      if (isCurrent()) setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        disabled={!allowed || busy}
        onClick={() => {
          void open(false);
        }}
      >
        {t('quoteResult.open')}
      </Button>
      <Button
        size="small"
        disabled={!allowed || busy}
        onClick={() => {
          void open(true);
        }}
      >
        {t('quoteResult.services')}
      </Button>
      {failed && <Alert severity="warning">{t('quoteResult.unavailable')}</Alert>}
    </Box>
  );
}
